import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Employee } from '../shared/model/employee.model';
import { EventService } from './event.service';
import { ConstantsService } from './constants.service';
import { Course, Discount, Product, ProductCategory, Resource, Session } from '../shared/model/resource.model';
import { ResourceType } from '../shared/model/resourceType.model';
import { Branch } from '../shared/model/Branch.model';
import { Terminal } from '../shared/model/Terminal.model';
import { AS365Defaults, KeyValuePair, LookupItem } from '../shared/model/AS365Defaults.model';
import { AptView } from '../shared/model/appointment-book.models';
import { CustomerSettingsData } from './customer-settings.service';
import { ReasonCode } from '../shared/model/ReasonCode';
import { Disclaimer } from '../shared/model/disclaimer.model';
import { Gender } from '../shared/model/gender.model';
import { take } from 'rxjs/operators';
import { AuthorizeService } from 'src/api-authorization/authorize.service';
import { CountryCode } from 'libphonenumber-js/types';
import { OnlineSalesSettings } from './online-sales.service';
import { MarketingSettingsData } from './marketing-settings.service';
import { PayPoint } from '../shared/model/PayPoint.model';


export enum enumAS365FeatureFlags {
  ClientDiscounts,
  Vouchers,
  ClientSignUp,
  MobileAccess,
  OnlineResourceAvailability,
}

export class AS365FeatureFlags {
  static CreateFrom(i: AS365FeatureFlags): AS365FeatureFlags {
    var featureFlag: AS365FeatureFlags = new AS365FeatureFlags();    
    if (i) {
      featureFlag.ClientDiscounts = i.ClientDiscounts;
      featureFlag.Vouchers = i.Vouchers;
      featureFlag.MobileAccess =i.MobileAccess;
      featureFlag.ClientSignUp= i.ClientSignUp;
      featureFlag.OnlineResourceAvailability = i.OnlineResourceAvailability;
    }
    return featureFlag;
  }

  ClientDiscounts: boolean = false;
  Vouchers: boolean = false;
  MobileAccess: boolean = false;
  ClientSignUp: boolean = false;
  OnlineResourceAvailability:boolean=false;
}

class DefaultsResponse {
  public StatusCode: number;
  public AS365HWCUrl: string;
  public WebAPIVersion: string;
  public SupportPhone: string;
  public Branches: Branch[];
  public Terminals: Terminal[];
  public Employees: Employee[];
  public Sessions: Session[];
  public Resources: Resource[];
  public ResourceTypes: ResourceType[];
  public Courses: Course[];
  public Products: Product[];
  public CustomerName = '';
  public TenantUid = '';
  public ReasonCodes: ReasonCode[];
  public CustomerSettings: CustomerSettingsData = new CustomerSettingsData();
  public MarketingSettings: MarketingSettingsData = new MarketingSettingsData();
  public OnlineSalesSettings: OnlineSalesSettings = new OnlineSalesSettings();
  public AptViews: AptView[] = [];
  public Disclaimers: Disclaimer[];
  public Discounts: Discount[];  
  public AuthEmployeeId: number;
  public ReportList: KeyValuePair[] = [];
  public GenderList: Gender[];
  public FeatureQueueManagerEnabled: boolean = false;
  public Locale: string;
  public AS365FeatureFlags: AS365FeatureFlags;
  public CampaignFilters: LookupItem[] = [];
  public ResourceAvailabilityPollingInterval: number = 5;
  public PayPoints: PayPoint[];
  public IPSCloudUrl: string;
  public LicenceExpiry: Date;
}
@Injectable({
  providedIn: 'root'
})
export class DefaultDataService {

  public defData: AS365Defaults = new AS365Defaults();

  constructor(public http: HttpClient, private events: EventService, private constants: ConstantsService, private auth:AuthorizeService) {
    console.log('Starting DefaultData');

    this.auth.isAuthenticated().pipe(take(1)).subscribe(r=>{
      if (r==true)
      this.GetDefaultData();
    });    
  }

  public GetDefaultData() {
    console.log('Fetching Default Data');
    const headers = { 'Content-Type': 'application/json' };    

    this.http.get<DefaultsResponse>('/api/Defaults', { headers })
      .pipe(take(1))
      .subscribe(model => {
        if (model.StatusCode == 200) {
          console.log('storing new Default Data');
          console.log('New Default Data', model);

          this.defData.AS365HWCUrl = model.AS365HWCUrl;
          this.defData.CustomerName = model.CustomerName;
          this.defData.TenantUid = model.TenantUid;
          this.defData.Branches = model.Branches.map(i => Branch.CreateFrom(i)); // This is needed so that create correct type of object with functions and methods and not just a pain old object
          this.defData.Employees = model.Employees.map(i => Employee.CreateFrom(i)).sort((a, b) => (a.FullName > b.FullName ? 1 : -1));
          this.defData.Terminals = model.Terminals.map(i => Terminal.CreateFrom(i)).sort((a, b) => (a.Name > b.Name ? 1 : -1));
          this.defData.Resources = model.Resources.map(i => Resource.CreateFrom(i)).sort((a, b) => (a.ResourceName > b.ResourceName ? 1 : -1));
          this.defData.Courses = model.Courses.map(i => Course.CreateFrom(i)).sort((a, b) => (a.CourseName > b.CourseName ? 1 : -1));
          this.defData.Sessions = model.Sessions.map(i => Session.CreateFrom(i)).sort((a, b) => (a.SessionName > b.SessionName ? 1 : -1));
          this.defData.Products = model.Products.map(i => Product.CreateFrom(i)).sort((a, b) => (a.ProductName > b.ProductName ? 1 : -1));
          this.defData.ResourceTypes = model.ResourceTypes.map(i => ResourceType.CreateFrom(i)).sort((a, b) => (a.Name > b.Name ? 1 : -1));
          this.defData.ReasonCodes = model.ReasonCodes.map(i => ReasonCode.CreateFrom(i)).sort((a, b) => (a.ReasonText > b.ReasonText ? 1 : -1));
          this.defData.Disclaimers = model.Disclaimers.map(i => Disclaimer.CreateFrom(i));
          this.defData.Discounts = model.Discounts.map(i => Discount.CreateFrom(i)).sort((a, b) => (a.Description > b.Description ? 1 : -1));;
          this.defData.customerSettings = model.CustomerSettings;
          this.defData.marketingSettings = model.MarketingSettings;
          this.defData.onlineSalesSettings = model.OnlineSalesSettings;
          this.defData.WebAPIVersion = model.WebAPIVersion;
          this.defData.SupportPhone = model.SupportPhone;
          this.defData.aptViews = model.AptViews.map(i => AptView.Create(i));
          this.defData.AuthEmployeeId = model.AuthEmployeeId || -1;
          this.defData.ReportList = model.ReportList.map(i => KeyValuePair.CreateFrom(i)).sort((a, b) => (a.Value > b.Value ? 1 : -1));
          this.defData.FeatureQueueManagerEnabled = model.FeatureQueueManagerEnabled;
          this.defData.Locale = model.Locale;
          this.defData.AS365FeatureFlags = AS365FeatureFlags.CreateFrom(model.AS365FeatureFlags);
          // this.defData.GenderList = model.GenderList.map(i => Gender.CreateFrom(i)).sort((a, b) => (a.Text > b.Text ? 1 : -1));
          // Save WebAPIVersion in global location
          this.events.WebAPIVersion = model.WebAPIVersion;
          this.constants.localeChanged$.next(<CountryCode>model.Locale.toUpperCase());
          this.defData.CampaignFilters = model.CampaignFilters.map(i => LookupItem.CreateFrom(i));
          this.defData.ResourceAvailabilityPollingInterval = model.ResourceAvailabilityPollingInterval;
          this.defData.PayPoints = model.PayPoints.map(i => PayPoint.CreateFrom(i));
          this.defData.IPSCloudUrl = model.IPSCloudUrl;
          this.defData.LicenceExpiry = model.LicenceExpiry;
          // Notify world of changes.
          console.log('Tell app about new Default Data');
          this.events.DefaultDataChanged(this.defData);
        }
      }, err => {
        console.log(err);
        throw err;
      });
  }

  public IsFeatureEnabled(featureName: enumAS365FeatureFlags ): boolean {
    switch(featureName)
    {
      case enumAS365FeatureFlags.ClientDiscounts: return this.defData.AS365FeatureFlags.ClientDiscounts;
      case enumAS365FeatureFlags.ClientSignUp: return this.defData.AS365FeatureFlags.ClientSignUp;
      case enumAS365FeatureFlags.Vouchers: return this.defData.AS365FeatureFlags.Vouchers;
      case enumAS365FeatureFlags.MobileAccess: return this.defData.AS365FeatureFlags.MobileAccess;
      default: return false
    }
  }

  public get AppTitle(): string {
    return this.defData?.AppTitle || this.constants.AppTitle;
  }

  public get WebAPIVersion(): string {
    return this.defData?.WebAPIVersion || '';
  }

  public get SupportPhone(): string{
    return this.defData?.SupportPhone || '0161 507 1598';
  }

  public get Copyright(): string {    
    var d:Date=new Date();
    return this.defData?.Copyright || 'SolarTrack Systems Ltd (c) 2021-'+d.getFullYear();
  }

  public get MinHWCVersion(): string {
    return this.defData?.MinHWCVersion || '2021.01.01.1';
  }

  public get AS365HWCUrl(): string {
    return this.defData?.AS365HWCUrl || 'https://as365hwc.local'  || 'https://as365hwc.lan';
  }

  public get Terminals(): Terminal[] {
    return this.defData?.Terminals || [];
  }

  public get Employees(): Employee[] {
    return this.defData?.Employees || [];
  }

  public get CustomerName(): string {
    return this.defData?.CustomerName || 'UNKNOWN CUSTOMER';
  }
}
